import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Navigate } from 'react-router-dom'

import { profileApi } from '@/api/endpoints'
import { Button } from '@/components/ui/Button'
import { PageSpinner } from '@/components/ui/Feedback'
import { useAuthStore } from '@/store/auth'
import { NaukriConnect } from './NaukriConnect'
import { ResumeUploadGate } from './ResumeUploadGate'
import { WizardShell } from './WizardShell'
import { ChecklistStep } from './steps/ChecklistStep'
import { ContactStep } from './steps/ContactStep'
import { LocationStep } from './steps/LocationStep'
import { PasswordStep } from './steps/PasswordStep'
import { SettingsStep } from './steps/SettingsStep'
import { WorkEligibilityStep } from './steps/WorkEligibilityStep'
import { useNavigateStep, useOnboardingState } from './useWizard'
import type { OnboardingStepKey, StepDefinition } from '@/types/api'

/** Step 1 shows location, then an optional Naukri panel before moving on. */
type Panel = 'step' | 'naukri'

const NAUKRI_COPY: Pick<StepDefinition, 'eyebrow' | 'title' | 'subtitle'> = {
  eyebrow: 'NAUKRI',
  title: 'Connect Naukri for India jobs',
  subtitle:
    'Thousands of roles on Naukri show up for people in India. Log into an account you already ' +
    'have, or create one, and we pull matches into your feed. Optional; you can do this later in Settings.',
}

const NAUKRI_CONNECT_COPY: Pick<StepDefinition, 'eyebrow' | 'title' | 'subtitle'> = {
  eyebrow: 'NAUKRI',
  title: 'Connect your Naukri account',
  subtitle:
    'Log into an existing account or create one. When you are done, we continue setup with no ' +
    'detour through Settings.',
}

export function OnboardingPage() {
  const { data: state, isLoading, isError, refetch } = useOnboardingState()
  const navigateStep = useNavigateStep()
  const [panel, setPanel] = useState<Panel>('step')
  const [resumeDeferred, setResumeDeferred] = useState(false)
  const [naukriStarted, setNaukriStarted] = useState(false)
  const [naukriExpanded, setNaukriExpanded] = useState(false)

  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: profileApi.get,
    staleTime: 30_000,
  })

  const definition = useMemo(
    () => state?.steps.find((step) => step.key === state.currentStep),
    [state],
  )

  if (isLoading) return <PageSpinner label="Loading your setup" />
  if (isError || !state || !definition) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-canvas px-4">
        <div className="card w-full max-w-[420px] px-6 py-8 text-center">
          <p className="text-[17px] font-medium text-ink">We could not load your setup</p>
          <p className="mt-1.5 text-[14px] text-ink-muted">Check your connection and try again.</p>
          <Button variant="secondary" className="mt-5" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      </div>
    )
  }

  if (state.isComplete) return <Navigate to="/dashboard" replace />

  // The wizard's copy assumes a resume is being parsed, so ask for one first.
  if (state.resumeStatus === 'missing' && !resumeDeferred) {
    return <ResumeUploadGate onDone={() => setResumeDeferred(true)} />
  }

  const goToPrevious = () => {
    if (panel === 'naukri') {
      setPanel('step')
      return
    }
    const previous = state.steps.find((step) => step.index === state.currentIndex - 1)
    if (previous) navigateStep.mutate(previous.key)
  }

  const advance = (from: OnboardingStepKey) => {
    // After location we offer Naukri once; everything else just advances.
    //
    // Note this runs *after* the step cursor has already moved on, so the panel
    // must not be reset by step changes — it closes only when the user connects,
    // skips, or goes back.
    if (from === 'location' && !naukriStarted) {
      setNaukriStarted(true)
      setPanel('naukri')
    }
  }

  if (panel === 'naukri') {
    // The intro headline until the user opens the account chooser, then the
    // sign-in headline — mirroring the two Naukri screens in the designs.
    const copy = naukriExpanded ? NAUKRI_CONNECT_COPY : NAUKRI_COPY
    return (
      <WizardShell
        state={state}
        eyebrow={copy.eyebrow}
        title={copy.title}
        subtitle={copy.subtitle}
        onBack={goToPrevious}
      >
        <NaukriConnect
          onExpand={setNaukriExpanded}
          onDone={() => {
            setPanel('step')
            setNaukriExpanded(false)
          }}
          onSkip={() => {
            setPanel('step')
            setNaukriExpanded(false)
          }}
        />
      </WizardShell>
    )
  }

  return (
    <WizardShell
      state={state}
      eyebrow={definition.eyebrow}
      title={definition.title}
      subtitle={definition.subtitle}
      onBack={goToPrevious}
      canGoBack={state.currentIndex > 1}
    >
      <StepBody
        step={state.currentStep}
        onDone={() => advance(state.currentStep)}
        profile={profile}
      />
    </WizardShell>
  )
}

function StepBody({
  step,
  onDone,
  profile,
}: {
  step: OnboardingStepKey
  onDone: () => void
  profile: Awaited<ReturnType<typeof profileApi.get>> | undefined
}) {
  switch (step) {
    case 'location':
      return <LocationStep onDone={onDone} initial={profile?.location ?? undefined} />
    case 'contact':
      return <ContactStep onDone={onDone} initial={profile?.contact ?? undefined} />
    case 'work_eligibility':
      return (
        <WorkEligibilityStep onDone={onDone} initial={profile?.workEligibility ?? undefined} />
      )
    case 'checklist':
      return <ChecklistStep onDone={onDone} initial={profile?.checklist ?? undefined} />
    case 'application_password':
      return <PasswordStep onDone={onDone} />
    case 'application_settings':
      return <SettingsStep />
    default:
      return null
  }
}

/** Used by the router to decide between the wizard and the dashboard. */
export function useOnboardingComplete(): boolean | null {
  const onboarding = useAuthStore((state) => state.onboarding)
  return onboarding ? onboarding.isComplete : null
}
